// HTML view of one AI Presence analysis (product or brand). Answers the core
// question — when shoppers ask AI what to buy, does the product appear and
// does AI recommend it? — per platform (Claude, Google Gemini, OpenAI):
// score strip with trend, platform table, ranked opportunities, the
// estimated shopping prompts (each expands to the sampled answers), share of
// voice, cited sources, accuracy of what AI says, branded research.
import React, { useState } from 'react';
import dayjs from 'dayjs';
import axiosInstant from '../../api';
import {
  AIP_OPPORTUNITY_TYPE_LABELS,
  AIP_PLATFORM_LABELS,
  AiPresenceAudit,
  AiPresenceBrandReport,
  AiPresenceProductReport,
  AipOpportunity,
  AipPromptRollup,
  AipResponse,
  aipSubjectLabel,
  isAipProductReport,
} from '../../types/aiPresence';

export const aipScoreTone = (score: number | null | undefined) => {
  if (score == null) return 'bg-surface-2 text-text-2';
  if (score >= 60) return 'bg-[#E8F5E9] text-[#2E7D32]';
  if (score >= 30) return 'bg-tint text-accent-text';
  return 'bg-[#FBEFEF] text-[#c62828]';
};

const fmt = (n: number | null | undefined) => (n == null ? '—' : Number(n).toLocaleString('en-US'));
const pctLabel = (n: number | null | undefined) => (n == null ? '—' : `${n}%`);

function Delta({ value }: { value: number | null | undefined }) {
  if (value == null) return null;
  let tone = 'text-muted';
  let label: string | number = '±0';
  if (value > 0) {
    tone = 'text-[#2E7D32]';
    label = `+${value}`;
  } else if (value < 0) {
    tone = 'text-[#c62828]';
    label = value;
  }
  return <span className={`text-[11px] font-semibold ${tone}`}>{label}</span>;
}

function mentionLabel(r: AipResponse): string {
  if (r.mentioned) return `Mentioned${r.rank ? ` · #${r.rank}` : ''}`;
  return r.mention_target === 'brand' ? 'Brand only' : 'Not mentioned';
}

function ScoreCard({
  label,
  value,
  caption,
  delta,
}: {
  label: string;
  value: number | null | undefined;
  caption: string;
  delta?: number | null;
}) {
  return (
    <div className="flex-1 min-w-[170px] border border-border-token rounded-xl p-4 bg-surface">
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-muted mb-2">{label}</div>
      <div className="flex items-baseline gap-2">
        <span className={`text-[28px] font-bold leading-none rounded-lg px-2 py-1 ${aipScoreTone(value)}`}>{value == null ? '—' : value}</span>
        <span className="text-[11px] text-muted">/ 100</span>
        <Delta value={delta} />
      </div>
      <div className="text-[12px] text-text-2 mt-2 leading-snug">{caption}</div>
    </div>
  );
}

function Section({ title, children, count, note }: { title: string; children: React.ReactNode; count?: number; note?: string }) {
  return (
    <section className="mt-7">
      <h3 className="text-[13px] font-bold uppercase tracking-[0.5px] text-text-1 mb-1">
        {title}
        {typeof count === 'number' ? <span className="ml-2 text-muted font-semibold">{count}</span> : null}
      </h3>
      {note ? <p className="text-[12px] text-muted mb-2.5">{note}</p> : <div className="mb-2.5" />}
      {children}
    </section>
  );
}

const Empty = ({ text }: { text: string }) => <p className="text-[12px] text-muted">{text}</p>;

const Chip = ({ children, tone = '' }: { children: React.ReactNode; tone?: string }) => (
  <span className={`text-[11px] font-semibold rounded-full px-2.5 py-1 whitespace-nowrap ${tone || 'bg-surface-2 text-text-2'}`}>{children}</span>
);

const REC_TONE: Record<string, string> = {
  explicit: 'bg-[#E8F5E9] text-[#2E7D32]',
  qualified: 'bg-tint text-accent-text',
  none: 'bg-surface-2 text-text-2',
};
const REC_LABEL: Record<string, string> = { explicit: 'Recommended', qualified: 'Listed as an option', none: 'Not recommended' };

function Bar({ value, tone = 'bg-accent-text' }: { value: number | null | undefined; tone?: string }) {
  const v = Math.max(0, Math.min(100, value || 0));
  return (
    <div className="h-2 w-full bg-surface-2 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${tone}`} style={{ width: `${v}%` }} />
    </div>
  );
}

function PlatformTable({ report }: { report: AiPresenceProductReport | AiPresenceBrandReport }) {
  const keys = Object.keys(report.platforms || {});
  if (!keys.length) return null;
  const trend = isAipProductReport(report as any) ? (report as AiPresenceProductReport).trend : null;
  return (
    <div className="border border-border-token rounded-xl overflow-hidden">
      <table className="w-full text-[13px]">
        <thead className="bg-surface-2 text-[11px] uppercase tracking-[0.5px] text-muted">
          <tr>
            <th className="text-left px-3 py-2 font-bold">Platform</th>
            <th className="text-left px-3 py-2 font-bold">AI Presence</th>
            <th className="text-left px-3 py-2 font-bold">Discovery</th>
            <th className="text-left px-3 py-2 font-bold">Recommendation</th>
            <th className="text-left px-3 py-2 font-bold hidden sm:table-cell">Tested</th>
          </tr>
        </thead>
        <tbody>
          {keys.map((k) => {
            const p = report.platforms[k];
            return (
              <tr key={k} className="border-t border-border-token">
                <td className="px-3 py-2.5 font-semibold text-text-1">{p.label || AIP_PLATFORM_LABELS[k] || k}</td>
                <td className="px-3 py-2.5">
                  <span className={`font-bold rounded-md px-1.5 py-0.5 ${aipScoreTone(p.score)}`}>{fmt(p.score)}</span>{' '}
                  {trend && trend.platforms[k] ? <Delta value={trend.platforms[k].delta} /> : null}
                </td>
                <td className="px-3 py-2.5 text-text-2">{fmt(p.discovery)}</td>
                <td className="px-3 py-2.5 text-text-2">{fmt(p.recommendation)}</td>
                <td className="px-3 py-2.5 text-[12px] text-muted hidden sm:table-cell">
                  {p.models && p.models.length ? p.models.join(', ') : ''}
                  {p.eligible != null ? ` · ${p.eligible} answers` : ''}
                  {p.errors ? ` · ${p.errors} failed` : ''}
                  {p.products_scored != null ? `${p.products_scored} products` : ''}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function OpportunityList({ items }: { items: AipOpportunity[] }) {
  if (!items || !items.length) return <Empty text="No open opportunities — AI already surfaces and recommends this product for the prompts tested." />;
  return (
    <ol className="flex flex-col gap-2.5">
      {items.map((o) => (
        <li key={o.key} className="border border-border-token rounded-lg p-3">
          <div className="flex flex-wrap items-center gap-2 mb-1">
            <Chip>{AIP_OPPORTUNITY_TYPE_LABELS[o.type] || o.type}</Chip>
            {o.product ? <Chip>{o.product}</Chip> : null}
            <span className="font-semibold text-text-1 text-[13px]">{o.title}</span>
          </div>
          <p className="text-[12px] text-text-2 m-0">{o.detail}</p>
          <p className="text-[12px] text-text-1 mt-1 mb-0">
            <span className="font-semibold">Do this: </span>
            {o.action}
          </p>
        </li>
      ))}
    </ol>
  );
}

function ResponseCard({ r }: { r: AipResponse }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-border-token rounded-lg p-2.5 bg-surface">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-[12px] text-text-1">
          {AIP_PLATFORM_LABELS[r.platform] || r.platform} · sample {r.sample + 1}
        </span>
        {r.status === 'error' ? (
          <Chip tone="bg-[#FBEFEF] text-[#c62828]">Failed</Chip>
        ) : (
          <>
            <Chip tone={r.mentioned ? 'bg-[#E8F5E9] text-[#2E7D32]' : ''}>
              {mentionLabel(r)}
            </Chip>
            {r.mentioned && r.recommendation ? <Chip tone={REC_TONE[r.recommendation]}>{REC_LABEL[r.recommendation]}</Chip> : null}
          </>
        )}
        {r.model ? <span className="text-[11px] text-muted">{r.model}</span> : null}
      </div>
      {r.judge && r.judge.summary ? <p className="text-[12px] text-text-2 mt-1.5 mb-0">{r.judge.summary}</p> : null}
      {r.competitors && r.competitors.length ? (
        <p className="text-[11.5px] text-muted mt-1 mb-0">Also named: {r.competitors.slice(0, 6).map((c) => c.name).join(', ')}</p>
      ) : null}
      {r.response_text ? (
        <button type="button" onClick={() => setOpen(!open)} className="text-[11.5px] text-accent-text mt-1 hover:underline">
          {open ? 'Hide answer' : 'Show full answer'}
        </button>
      ) : null}
      {open && r.response_text ? (
        <pre className="whitespace-pre-wrap font-sans text-[12px] text-text-2 bg-surface-2 rounded-md p-2.5 mt-1.5 max-h-[360px] overflow-auto">
          {r.response_text}
        </pre>
      ) : null}
      {open && r.sources && r.sources.length ? (
        <ul className="mt-1.5 flex flex-col gap-0.5">
          {r.sources.slice(0, 12).map((s, i) => (
            <li key={i} className="text-[11.5px] truncate">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-accent-text hover:underline">
                {s.title || s.url}
              </a>
              {s.cited ? <span className="text-muted"> · cited</span> : null}
              {s.own ? <span className="text-[#2E7D32]"> · {s.own === 'own_pdp' ? 'your PDP' : 'your site'}</span> : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function PromptRow({
  p,
  platforms,
  previousRate,
  responsesEndpoint,
}: {
  p: AipPromptRollup;
  platforms: string[];
  previousRate?: number | null;
  responsesEndpoint?: (promptKey: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [answers, setAnswers] = useState<AipResponse[] | null>(null);
  const [failed, setFailed] = useState(false);
  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && !answers && responsesEndpoint) {
      axiosInstant
        .get(responsesEndpoint(p.key))
        .then((res) => setAnswers(res.data as AipResponse[]))
        .catch(() => setFailed(true));
    }
  };
  const delta = previousRate != null && p.mention_rate != null ? p.mention_rate - previousRate : null;
  return (
    <div className="border border-border-token rounded-lg">
      <button type="button" onClick={toggle} className="w-full text-left p-3 hover:bg-surface-2 rounded-lg">
        <div className="flex flex-wrap items-center gap-2 mb-1">
          <Chip tone={p.branded ? 'bg-tint text-accent-text' : ''}>{p.intent_label}</Chip>
          {p.est_monthly_searches ? (
            <span className="text-[11px] text-muted" title={p.keyword ? `Keyword Planner: “${p.keyword}”` : undefined}>
              ~{fmt(p.est_monthly_searches)} searches/mo
            </span>
          ) : null}
          <span className="ml-auto text-[11px] text-muted">{open ? '▲' : '▼'}</span>
        </div>
        <div className="text-[13px] text-text-1 font-medium">{p.text}</div>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1.5 text-[11.5px] text-text-2">
          {platforms.map((k) => {
            const x = p.platforms[k];
            if (!x) return null;
            return (
              <span key={k}>
                {AIP_PLATFORM_LABELS[k] || k}:{' '}
                <span className="font-semibold">
                  {x.judged ? `${x.mentioned}/${x.judged}` : '—'}
                </span>
                {x.explicit ? <span className="text-[#2E7D32]"> · {x.explicit} recommended</span> : null}
              </span>
            );
          })}
          {delta != null && delta !== 0 ? (
            <span>
              vs last run <Delta value={delta} />
            </span>
          ) : null}
        </div>
      </button>
      {open ? (
        <div className="px-3 pb-3 flex flex-col gap-2">
          {!responsesEndpoint ? <Empty text="Answers aren't available here." /> : null}
          {responsesEndpoint && !answers && !failed ? <Empty text="Loading answers…" /> : null}
          {failed ? <Empty text="Couldn't load the answers." /> : null}
          {answers && !answers.length ? <Empty text="No answers stored for this prompt." /> : null}
          {answers ? answers.map((r) => <ResponseCard key={r.id} r={r} />) : null}
        </div>
      ) : null}
    </div>
  );
}

function ProductBody({
  audit,
  report,
  responsesEndpoint,
}: {
  audit: AiPresenceAudit;
  report: AiPresenceProductReport;
  responsesEndpoint?: (promptKey: string) => string;
}) {
  const platforms = Object.keys(report.platforms || {});
  const unbranded = report.prompts.filter((p) => !p.branded);
  const branded = report.prompts.filter((p) => p.branded);
  const { competitors: sov, sources: src, trend } = report;
  return (
    <>
      <div className="flex flex-wrap gap-3 mt-4">
        <ScoreCard
          label="AI Presence Score"
          value={audit.overall_score}
          delta={trend ? trend.overall.delta : null}
          caption="50% Discovery + 50% Recommendation, averaged across platforms."
        />
        <ScoreCard
          label="Discovery"
          value={audit.discovery_score}
          delta={trend ? trend.discovery.delta : null}
          caption="Share of unbranded answers that meaningfully mention the product."
        />
        <ScoreCard
          label="Recommendation"
          value={audit.recommendation_score}
          delta={trend ? trend.recommendation.delta : null}
          caption="Average credit: recommended 100, listed as an option 50, otherwise 0."
        />
      </div>
      {trend ? (
        <p className="text-[12px] text-muted mt-2 mb-0">
          Change since the run of {dayjs(trend.previous_scored_at).format('MMM D, YYYY')} on the same benchmark prompts.
        </p>
      ) : null}

      <Section title="By platform">
        <PlatformTable report={report} />
      </Section>

      <Section title="Opportunities" count={(report.opportunities || []).length}>
        <OpportunityList items={report.opportunities || []} />
      </Section>

      <Section
        title={`${report.label || 'Estimated shopping prompts'} · unbranded`}
        count={unbranded.length}
        note="Questions shoppers likely ask AI on the way to buying, built from Keyword Planner demand, Consumer Voice and the product page. These drive the scores. Open a prompt to read the answers."
      >
        <div className="flex flex-col gap-2">
          {unbranded.map((p) => (
            <PromptRow
              key={p.key}
              p={p}
              platforms={platforms}
              previousRate={trend ? trend.prompt_mention_rate[p.key] : null}
              responsesEndpoint={responsesEndpoint}
            />
          ))}
        </div>
      </Section>

      <Section title="Competitor visibility" note={`Share of the ${sov.eligible} unbranded answers that name each product or brand.`}>
        <div className="flex flex-col gap-2">
          <div className="grid grid-cols-[minmax(0,1fr)_120px_48px] items-center gap-3">
            <span className="text-[13px] font-semibold text-text-1 truncate">{sov.us.name} (you)</span>
            <Bar value={sov.us.appearance_rate} tone="bg-[#2E7D32]" />
            <span className="text-[12px] text-text-1 font-semibold text-right">{pctLabel(sov.us.appearance_rate)}</span>
          </div>
          {sov.competitors.slice(0, 10).map((c) => (
            <div key={c.name} className="grid grid-cols-[minmax(0,1fr)_120px_48px] items-center gap-3">
              <span className="text-[13px] text-text-2 truncate" title={c.brand || undefined}>{c.name}</span>
              <Bar value={c.appearance_rate} />
              <span className="text-[12px] text-text-2 text-right">{pctLabel(c.appearance_rate)}</span>
            </div>
          ))}
          {!sov.competitors.length ? <Empty text="No competitors named in the answers." /> : null}
          {sov.siblings && sov.siblings.length ? (
            <p className="text-[12px] text-muted mt-1 mb-0">
              Your other products AI named instead: {sov.siblings.map((s) => `${s.name} (${pctLabel(s.appearance_rate)})`).join(', ')}
            </p>
          ) : null}
        </div>
      </Section>

      <Section
        title="Cited sources"
        note={`Your product page was cited in ${fmt(src.own_pdp_cited_answers || 0)} answers${src.own_pdp_cited_rate != null ? ` (${src.own_pdp_cited_rate}%)` : ''}. The sites AI relies on for this category:`}
      >
        {src.domains && src.domains.length ? (
          <div className="border border-border-token rounded-xl overflow-hidden">
            <table className="w-full text-[12.5px]">
              <thead className="bg-surface-2 text-[11px] uppercase tracking-[0.5px] text-muted">
                <tr>
                  <th className="text-left px-3 py-2 font-bold">Site</th>
                  <th className="text-left px-3 py-2 font-bold">Answers</th>
                  <th className="text-left px-3 py-2 font-bold">With you</th>
                  <th className="text-left px-3 py-2 font-bold">Without you</th>
                </tr>
              </thead>
              <tbody>
                {src.domains.slice(0, 12).map((d) => (
                  <tr key={d.domain} className="border-t border-border-token">
                    <td className="px-3 py-2 text-text-1">
                      {d.domain}
                      {d.own ? <span className="text-[11px] text-[#2E7D32]"> · {d.own === 'own_pdp' ? 'your PDP' : 'your site'}</span> : null}
                    </td>
                    <td className="px-3 py-2 text-text-2">{d.answers}</td>
                    <td className="px-3 py-2 text-text-2">{d.with_us}</td>
                    <td className="px-3 py-2 text-text-2">{d.without_us}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <Empty text="No sources were returned with the answers." />
        )}
      </Section>

      <Section
        title="What AI says about the product"
        note={`${report.claims.counts.accurate} accurate, ${report.claims.counts.inaccurate} inaccurate, ${report.claims.counts.unverifiable} not checkable against the product page.`}
      >
        {report.claims.inaccurate.length ? (
          <ul className="flex flex-col gap-2">
            {report.claims.inaccurate.map((c, i) => (
              <li key={i} className="border border-[#f3cfcf] bg-[#FBEFEF] dark:bg-transparent rounded-lg p-2.5">
                <div className="text-[13px] text-text-1">“{c.claim}”</div>
                {c.note ? <div className="text-[12px] text-text-2 mt-0.5">{c.note}</div> : null}
                <div className="text-[11px] text-muted mt-0.5">
                  {c.platforms.map((k) => AIP_PLATFORM_LABELS[k] || k).join(', ')} · {c.count}×
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <Empty text="No inaccurate claims found." />
        )}
      </Section>

      {branded.length ? (
        <Section title="Branded research" count={branded.length} note="How AI describes the product when shoppers ask about it by name. These prompts don't affect the scores.">
          <div className="flex flex-col gap-2">
            {branded.map((p) => (
              <PromptRow key={p.key} p={p} platforms={platforms} responsesEndpoint={responsesEndpoint} />
            ))}
          </div>
        </Section>
      ) : null}

      {report.meta.competitors_tracked && report.meta.competitors_tracked.length ? (
        <p className="text-[11.5px] text-muted mt-6 mb-0">Competitors tracked: {report.meta.competitors_tracked.join(', ')}</p>
      ) : null}
    </>
  );
}

function BrandBody({ audit, report, onOpenProduct }: { audit: AiPresenceAudit; report: AiPresenceBrandReport; onOpenProduct?: (auditId: number) => void }) {
  return (
    <>
      <div className="flex flex-wrap gap-3 mt-4">
        <ScoreCard label="AI Presence Score" value={audit.overall_score} caption={`Mean of ${report.products_scored} scored products.`} />
        <ScoreCard label="Discovery" value={audit.discovery_score} caption="How often the brand's products appear in unbranded answers." />
        <ScoreCard label="Recommendation" value={audit.recommendation_score} caption="How strongly AI recommends them." />
      </div>
      <Section title="By platform">
        <PlatformTable report={report} />
      </Section>
      <Section title="Products" count={report.per_product.length}>
        <div className="border border-border-token rounded-xl overflow-hidden">
          <table className="w-full text-[13px]">
            <thead className="bg-surface-2 text-[11px] uppercase tracking-[0.5px] text-muted">
              <tr>
                <th className="text-left px-3 py-2 font-bold">Product</th>
                <th className="text-left px-3 py-2 font-bold">AI Presence</th>
                <th className="text-left px-3 py-2 font-bold hidden sm:table-cell">Most visible competitor</th>
              </tr>
            </thead>
            <tbody>
              {report.per_product.map((p) => (
                <tr key={p.audit_id} className="border-t border-border-token">
                  <td className="px-3 py-2.5">
                    {onOpenProduct && p.status === 'ready' ? (
                      <button type="button" onClick={() => onOpenProduct(p.audit_id)} className="text-accent-text hover:underline text-left">
                        {p.title}
                      </button>
                    ) : (
                      <span className="text-text-1">{p.title}</span>
                    )}
                    {p.status === 'error' ? <span className="text-[11px] text-[#c62828]"> · failed</span> : null}
                  </td>
                  <td className="px-3 py-2.5">
                    <span className={`font-bold rounded-md px-1.5 py-0.5 ${aipScoreTone(p.overall_score)}`}>{fmt(p.overall_score)}</span>
                  </td>
                  <td className="px-3 py-2.5 text-text-2 hidden sm:table-cell">
                    {p.top_competitor ? `${p.top_competitor.name} (${pctLabel(p.top_competitor.appearance_rate)})` : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Section>
      <Section title="Top opportunities" count={report.top_opportunities.length}>
        <OpportunityList items={report.top_opportunities} />
      </Section>
      <Section title="Competitors across products">
        {report.competitors.length ? (
          <div className="flex flex-col gap-2">
            {report.competitors.slice(0, 10).map((c) => (
              <div key={c.name} className="grid grid-cols-[minmax(0,1fr)_120px_120px] items-center gap-3">
                <span className="text-[13px] text-text-2 truncate">{c.name}</span>
                <Bar value={c.avg_appearance_rate} />
                <span className="text-[12px] text-text-2">
                  {c.avg_appearance_rate}% · {c.products} product{c.products === 1 ? '' : 's'}
                </span>
              </div>
            ))}
          </div>
        ) : (
          <Empty text="No competitors named." />
        )}
      </Section>
      <Section title="Cited sources">
        {report.sources.domains.length ? (
          <p className="text-[12.5px] text-text-2 m-0">
            {report.sources.domains.slice(0, 12).map((d) => `${d.domain} (${d.answers})`).join(' · ')}
          </p>
        ) : (
          <Empty text="No sources were returned with the answers." />
        )}
      </Section>
      {report.inaccurate_claims.length ? (
        <Section title="Inaccurate claims">
          <ul className="flex flex-col gap-1.5">
            {report.inaccurate_claims.map((c, i) => (
              <li key={i} className="text-[12.5px] text-text-2">
                <span className="font-semibold text-text-1">{c.product}:</span> “{c.claim}”{c.note ? ` — ${c.note}` : ''}
              </li>
            ))}
          </ul>
        </Section>
      ) : null}
    </>
  );
}

export function AiPresenceReportView({
  audit,
  onOpenProduct,
  responsesEndpoint,
  onCreateStrategy,
}: {
  audit: AiPresenceAudit;
  // Brand reports: open a product run in place.
  onOpenProduct?: (auditId: number) => void;
  // Product reports: GET endpoint for one prompt's answers.
  responsesEndpoint?: (promptKey: string) => string;
  // Opens the strategy wizard with this analysis as its suggestion source.
  onCreateStrategy?: () => void;
}) {
  const { report } = audit;
  const when = audit.scored_at ? dayjs(audit.scored_at).format('MMM D, YYYY') : '';
  const meta = report && isAipProductReport(report) ? report.meta : null;
  const renderBody = () => {
    if (audit.status !== 'ready' || !report) return <p className="text-[13px] text-text-2 mt-6">This analysis isn&apos;t ready.</p>;
    if (isAipProductReport(report)) return <ProductBody audit={audit} report={report} responsesEndpoint={responsesEndpoint} />;
    return <BrandBody audit={audit} report={report as AiPresenceBrandReport} onOpenProduct={onOpenProduct} />;
  };
  return (
    <div className="p-6 sm:p-8 text-text-1">
      <div className="text-[11px] font-bold uppercase tracking-[0.5px] text-muted">AI Presence Analysis</div>
      <h2 className="text-[22px] font-bold mt-1 mb-1">{aipSubjectLabel(audit)}</h2>
      <p className="text-[12.5px] text-muted m-0">
        When shoppers ask AI what to buy, does this {audit.kind === 'brand' ? 'brand' : 'product'} appear — and does AI recommend it?
        {when ? ` · ${when}` : ''}
      </p>
      {meta ? (
        <p className="text-[11.5px] text-muted mt-1 mb-0">
          {meta.surface_label} · {meta.unbranded_count} category + {meta.branded_count} product question
          {meta.branded_count === 1 ? '' : 's'}{meta.samples > 1 ? `, each asked ${meta.samples} times` : ''}
          {meta.prompt_set && meta.prompt_set.version ? ` · benchmark v${meta.prompt_set.version}` : ''}
        </p>
      ) : null}
      {meta && meta.category ? (
        <p className="text-[11.5px] text-muted mt-1 mb-0">
          The category questions are asked once for all of this brand&apos;s {meta.category.name} products
          {meta.category.answered_at ? ` (answered ${dayjs(meta.category.answered_at).format('MMM D, YYYY')})` : ''}, so they compare on the
          same answers.
        </p>
      ) : null}
      {onCreateStrategy && audit.status === 'ready' ? (
        <div className="mt-4 border border-border-token rounded-xl p-4 bg-tint flex flex-wrap items-center justify-between gap-3">
          <div className="text-[13px] text-text-1">
            <strong>Turn this into a strategy.</strong> We’ll suggest the objective and secondary objective these AI answers point to.
          </div>
          <button
            type="button"
            onClick={onCreateStrategy}
            className="text-[12px] font-semibold text-white bg-primary rounded-md px-3.5 py-2 cursor-pointer hover:opacity-90"
          >
            Create strategy
          </button>
        </div>
      ) : null}
      {renderBody()}
      <p className="text-[11px] text-muted mt-8 mb-0">
        Answers come from each vendor&apos;s API with web search on, which approximates (but isn&apos;t identical to) the consumer apps.
        AI answers vary from run to run, so the score spreads across several different shopping questions rather than one.
      </p>
    </div>
  );
}
